import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "..\/..\/interfaces\/DecisionPriority";
import { GameState, IMove, IPiece, Position } from "..\/..\/..\/..\/types";
import {
  cloneFullGameState,
  applyMoveWithSnakeRespawn,
  validateMeadowEffects,
  getPieceFromPosition,
} from "..\/..\/..\/utils\/MCTSUtils";
import { MCTSMoveGenerationService } from "..\/..\/..\/utils\/MCTSMoveGenerationService";

/**
 * Priority 8: Two Turn Race Block
 *
 * Analyzes if the light player (human) has any 2-turn winning paths by:
 * 1. Simulating all light king moves (1st ply)
 * 2. For each light move, simulating all dark responses (2nd ply)
 * 3. Pruning paths where dark captures light king, then checking if light king can reach row 7 (3rd ply)
 *
 * Uses proper game state simulation with deep copying for accurate analysis.
 */
export class TwoTurnRaceBlockPriority extends BaseDecisionPriority {
  readonly priority = 10;
  readonly name = "Two Turn Race Block";
  readonly description = "Analyze potential 2-turn race paths for light player";

  private analysisLogs: string[] = [];

  isRelevant(context: DecisionContext): boolean {
    return true; // Always analyze potential light race paths
  }

  analyze(context: DecisionContext): DecisionResult {
    this.analysisLogs = [];
    // 2-turn race analysis starting

    const gameState = context.gameState;

    // Only show board state in extreme debugging mode (commented out for normal use)
    // Uncomment this block only when debugging race analysis issues
    /*
    this.addLog("🔍 BOARD STATE DEBUG: Current piece positions:");
    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        const square = gameState.board[row][col];
        if (square && square.Pieces && square.Pieces.length > 0) {
          for (const piece of square.Pieces) {
            this.addLog(
              `🔍   [${row},${col}]: ${piece.isLight ? "Light" : "Dark"} ${
                piece.type
              }${piece.isKing ? " (KING)" : ""}`
            );
          }
        }
      }
    }
    */

    // Add safety check for board structure
    if (!gameState || !gameState.board || !Array.isArray(gameState.board)) {
      this.addLog("Invalid game state or board structure");
      return this.createNoActionResult("Invalid game state");
    }

    // CRITICAL FIX: Check if any of our moves would create winning paths for Light
    const darkMoves = this.filterToDarkMoves(
      context.allPossibleMoves,
      gameState
    );

    const dangerousMoves: MoveInfo[] = [];

    for (const darkMove of darkMoves) {
      // Simulate this Dark move
      const hypotheticalState = cloneFullGameState(gameState);
      hypotheticalState.isLightTurn = false; // It's Dark's turn

      const darkPiece = getPieceFromPosition(
        hypotheticalState,
        darkMove.fromPosition
      );
      if (!darkPiece) {
        continue;
      }

      // Apply the Dark move
      applyMoveWithSnakeRespawn(
        hypotheticalState,
        darkMove.fromPosition,
        darkMove.move,
        darkPiece
      );
      validateMeadowEffects(hypotheticalState);
      // hypotheticalState.isLightTurn is now true (Light's turn)

      // Now check if Light has 2-turn winning paths in this hypothetical position
      const winningPathsAfterDarkMove =
        this.analyzeRaceThreatsInPosition(hypotheticalState);

      if (winningPathsAfterDarkMove.length > 0) {
        dangerousMoves.push(darkMove);
      }
    }

    if (dangerousMoves.length > 0) {
      // Filter out the dangerous moves
      const safeMoves = context.allPossibleMoves.filter(
        (move) =>
          !dangerousMoves.some(
            (dangerous) =>
              dangerous.fromPosition.row === move.fromPosition.row &&
              dangerous.fromPosition.col === move.fromPosition.col &&
              dangerous.move.position.row === move.move.position.row &&
              dangerous.move.position.col === move.move.position.col
          )
      );

      if (safeMoves.length === 0) {
        return this.createNoActionResult(
          "All moves create Light race threats - position may be lost"
        );
      }

      // Update the context to only include safe moves
      context.allPossibleMoves = safeMoves;

      return this.createNoActionResult(
        `Filtered out ${dangerousMoves.length} moves that would create Light race threats`
      );
    }
    return this.runOriginalRaceAnalysis(gameState, context);
  }

  /**
   * NEW METHOD: Analyze race threats in a given position
   */
  private analyzeRaceThreatsInPosition(gameState: GameState): string[] {
    const lightKing = this.findLightKing(gameState);
    if (!lightKing) {
      return [];
    }

    const lightKingMoves = this.getLightKingMoves(gameState, lightKing);
    const winningPaths: string[] = [];

    for (const lightMove of lightKingMoves) {
      const pathResult = this.simulate3PlyRacePath(gameState, lightMove);
      if (pathResult.hasWinningPath) {
        winningPaths.push(pathResult.pathDescription);
      }
    }

    return winningPaths;
  }

  /**
   * RENAMED: Original race analysis logic (as fallback)
   */
  private runOriginalRaceAnalysis(
    gameState: GameState,
    context: DecisionContext
  ): DecisionResult {
    const lightKing = this.findLightKing(gameState);

    if (!lightKing) {
      return this.createNoActionResult("No light king found");
    }

    // Get all possible light king moves (1st ply)
    const lightKingMoves = this.getLightKingMoves(gameState, lightKing);

    const winningPaths: string[] = [];

    // Test each light king move
    for (const lightMove of lightKingMoves) {
      const pathResult = this.simulate3PlyRacePath(gameState, lightMove);

      if (pathResult.hasWinningPath) {
        winningPaths.push(pathResult.pathDescription);
      }
    }

    if (winningPaths.length > 0) {
      this.addLog(
        `Race threat detected: ${winningPaths.length} winning path(s)`
      );

      // Get the winning moves for defensive analysis
      const winningMoves = lightKingMoves.filter((move) => {
        const pathResult = this.simulate3PlyRacePath(gameState, move);
        return pathResult.hasWinningPath;
      });

      // Run defensive simulation to find blocking moves
      const defensiveMove = this.findDefensiveMove(
        gameState,
        winningMoves,
        context.allPossibleMoves
      );

      if (defensiveMove) {
        this.addLog(`Defensive move found: blocking race threat`);
        return this.createEarlyExitResult(
          defensiveMove,
          `Block 2-turn race threat with defensive move`,
          {
            logs: this.analysisLogs,
            winningPaths,
            defensiveMove: {
              fromPosition: defensiveMove.fromPosition,
              toPosition: defensiveMove.move.position,
              description: `Move to block Light's race victory`,
            },
          }
        );
      } else {
        this.addLog("No defensive moves found - threat cannot be blocked");
        return this.createActionResult(winningPaths);
      }
    } else {
      this.addLog("No 2-turn race threats detected");
      return this.createNoActionResult("No 2-turn race threats detected");
    }
  }

  private findLightKing(gameState: GameState): {
    piece: IPiece;
    position: Position;
  } | null {
    try {
      // Zodiac board is 5x8 (5 cols, 8 rows): from (0,0) bottom-left to (7,4) top-right
      for (let row = 0; row < gameState.board.length; row++) {
        if (!gameState.board[row]) continue;

        for (let col = 0; col < gameState.board[row].length; col++) {
          const square = gameState.board[row][col];
          if (!square || !square.Pieces || !Array.isArray(square.Pieces))
            continue;

          const pieces = square.Pieces;
          for (const piece of pieces) {
            if (piece && piece.isLight && piece.isKing) {
              return { piece, position: { row, col } };
            }
          }
        }
      }
    } catch (error) {
    }
    return null;
  }

  private getLightKingMoves(
    gameState: GameState,
    lightKing: { piece: IPiece; position: Position }
  ): MoveInfo[] {
    // Create a temporary state where it's light's turn
    const tempState = cloneFullGameState(gameState);
    tempState.isLightTurn = true;

    const moveService = MCTSMoveGenerationService.getInstance();
    const allMoves = moveService.getAllMoves(tempState);

    // Filter to only light king moves - use position and piece type instead of ID
    return allMoves.filter((moveInfo) => {
      // Check if move is from the light king's position
      if (
        moveInfo.fromPosition.row !== lightKing.position.row ||
        moveInfo.fromPosition.col !== lightKing.position.col
      ) {
        return false;
      }

      // Double-check the piece at that position is light and king
      const piece = getPieceFromPosition(tempState, moveInfo.fromPosition);
      return piece && piece.isLight && piece.isKing;
    });
  }

  private simulate3PlyRacePath(
    gameState: GameState,
    lightMove: MoveInfo
  ): {
    hasWinningPath: boolean;
    pathDescription: string;
    blockReason?: string;
  } {
    // 1st PLY: Apply light king move
    const stateAfterLightMove = cloneFullGameState(gameState);
    stateAfterLightMove.isLightTurn = true; // Force light's turn for simulation

    const lightPiece = getPieceFromPosition(
      stateAfterLightMove,
      lightMove.fromPosition
    );
    if (!lightPiece) {
      return {
        hasWinningPath: false,
        pathDescription: "",
        blockReason: "Invalid light piece for move",
      };
    }

    // Apply light move
    applyMoveWithSnakeRespawn(
      stateAfterLightMove,
      lightMove.fromPosition,
      lightMove.move,
      lightPiece
    );
    validateMeadowEffects(stateAfterLightMove);
    stateAfterLightMove.isLightTurn = false; // Now dark's turn

    const lightKingNewPosition = lightMove.move.position;

    // 2nd PLY: Get all dark responses and test each one
    const moveService = MCTSMoveGenerationService.getInstance();
    const darkResponses = moveService.getAllMoves(stateAfterLightMove);

    if (darkResponses.length === 0) {
      // No dark responses - check if light can win immediately
      return this.checkImmediateWin(
        stateAfterLightMove,
        lightKingNewPosition,
        lightMove
      );
    }

    // Test each dark response - we need ALL responses to fail for light to have a winning path
    for (const darkResponse of darkResponses) {
      const darkPiece = getPieceFromPosition(
        stateAfterLightMove,
        darkResponse.fromPosition
      );
      // Only log individual responses if they're potentially suspicious or result in captures
      // (Most verbose debugging removed for performance)

      // Check if this dark move would capture the light king directly
      if (
        darkResponse.move.position.row === lightKingNewPosition.row &&
        darkResponse.move.position.col === lightKingNewPosition.col
      ) {
        // This dark move captures the light king - path is blocked
        return {
          hasWinningPath: false,
          pathDescription: "",
          blockReason: `Light king can be captured by dark ${
            darkPiece?.type || "piece"
          }`,
        };
      }

      // Apply dark response and check if light king survives
      const stateAfterDarkResponse = cloneFullGameState(stateAfterLightMove);
      const testDarkPiece = getPieceFromPosition(
        stateAfterDarkResponse,
        darkResponse.fromPosition
      );

      if (!testDarkPiece) {
        continue; // Skip invalid responses silently
      }

      applyMoveWithSnakeRespawn(
        stateAfterDarkResponse,
        darkResponse.fromPosition,
        darkResponse.move,
        testDarkPiece
      );
      validateMeadowEffects(stateAfterDarkResponse);
      stateAfterDarkResponse.isLightTurn = true; // Back to light's turn

      // Double-check if dark captured the light king (backup verification)
      const lightKingStillExists = this.isLightKingAtPosition(
        stateAfterDarkResponse,
        lightKingNewPosition
      );
      if (!lightKingStillExists) {
        // Light king was captured - this path is blocked
        return {
          hasWinningPath: false,
          pathDescription: "",
          blockReason: `Light king captured by dark ${testDarkPiece.type}`,
        };
      }

      // 3rd PLY: Check if light king can reach row 7 (victory)
      const canReachVictory = this.canLightKingReachRow7(
        stateAfterDarkResponse,
        lightKingNewPosition
      );
      if (!canReachVictory) {
        // Cannot reach victory after this dark response
        return {
          hasWinningPath: false,
          pathDescription: "",
          blockReason: `Cannot reach row 7 after dark ${testDarkPiece.type} response`,
        };
      }
    }

    // If we reach here, light king survives all dark responses and can reach row 7

    // Find the actual winning squares on row 7
    const tempStateForWinSquares = cloneFullGameState(stateAfterLightMove);
    tempStateForWinSquares.isLightTurn = true;
    const winningSquares = this.getRow7WinningSquares(
      tempStateForWinSquares,
      lightMove.move.position
    );

    const winSquaresStr = winningSquares
      .map((pos: Position) => `[7,${pos.col}]`)
      .join(" or ");
    const pathDesc = `[${lightMove.fromPosition.row},${lightMove.fromPosition.col}]->[${lightMove.move.position.row},${lightMove.move.position.col}]->${winSquaresStr}`;
    return {
      hasWinningPath: true,
      pathDescription: `Light player has a winning path ${pathDesc}`,
    };
  }

  private checkImmediateWin(
    gameState: GameState,
    kingPosition: Position,
    lightMove: MoveInfo
  ): {
    hasWinningPath: boolean;
    pathDescription: string;
    blockReason?: string;
  } {
    // Set to light's turn and check if king can reach row 7
    const tempState = cloneFullGameState(gameState);
    tempState.isLightTurn = true;

    const canWin = this.canLightKingReachRow7(tempState, kingPosition);

    if (canWin) {
      const winningSquares = this.getRow7WinningSquares(
        tempState,
        kingPosition
      );
      const winSquaresStr = winningSquares
        .map((pos: Position) => `[7,${pos.col}]`)
        .join(" or ");
      const pathDesc = `[${lightMove.fromPosition.row},${lightMove.fromPosition.col}]->[${lightMove.move.position.row},${lightMove.move.position.col}]->${winSquaresStr}`;
      return {
        hasWinningPath: true,
        pathDescription: `Light player has a winning path ${pathDesc} (no dark responses possible)`,
      };
    }

    return {
      hasWinningPath: false,
      pathDescription: "",
      blockReason: "Cannot reach row 7 even without dark responses",
    };
  }

  private isLightKingAtPosition(
    gameState: GameState,
    position: Position
  ): boolean {
    const pieces = gameState.board[position.row][position.col].Pieces;
    return pieces.some((piece) => piece.isLight && piece.isKing);
  }

  private canLightKingReachRow7(
    gameState: GameState,
    kingPosition: Position
  ): boolean {
    const moveService = MCTSMoveGenerationService.getInstance();
    const allMoves = moveService.getAllMoves(gameState);

    // Find moves from the light king's current position
    const kingMoves = allMoves.filter(
      (moveInfo) =>
        moveInfo.fromPosition.row === kingPosition.row &&
        moveInfo.fromPosition.col === kingPosition.col
    );

    // Check if any move reaches row 7
    return kingMoves.some((move) => move.move.position.row === 7);
  }

  private getRow7WinningSquares(
    gameState: GameState,
    kingPosition: Position
  ): Position[] {
    const moveService = MCTSMoveGenerationService.getInstance();
    const allMoves = moveService.getAllMoves(gameState);

    // Find moves from the light king's current position that reach row 7
    const kingMoves = allMoves.filter(
      (moveInfo) =>
        moveInfo.fromPosition.row === kingPosition.row &&
        moveInfo.fromPosition.col === kingPosition.col &&
        moveInfo.move.position.row === 7
    );

    // Return the winning positions on row 7
    return kingMoves.map((move) => move.move.position);
  }

  private createActionResult(winningPaths: string[]): DecisionResult {
    return {
      shouldEarlyExit: true,
      reasoning: `ALERT: Light player has ${winningPaths.length} 2-turn winning path(s)`,
      priority: this.priority,
      diagnostics: {
        logs: this.analysisLogs,
        winningPaths,
      },
    };
  }

  protected createNoActionResult(reason: string): DecisionResult {
    return {
      shouldEarlyExit: false,
      reasoning: reason,
      priority: this.priority,
      diagnostics: {
        logs: this.analysisLogs,
      },
    };
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
  }

  /**
   * 3-Ply Defensive Simulation
   * Ply 1: Test every possible Dark move (RESPECTING HIGHER PRIORITY FILTERS)
   * Ply 2: Simulate Light king moving to threat square
   * Ply 3: Check if Dark can capture Light king
   *
   * ENHANCED: Prefers non-hanging defensive moves when multiple options exist
   */
  private findDefensiveMove(
    gameState: GameState,
    winningMoves: MoveInfo[],
    filteredMoves: MoveInfo[]
  ): MoveInfo | null {
    if (winningMoves.length === 0) return null;

    // CRITICAL: Use only the moves that survived higher priority filtering
    const darkMoves = this.filterToDarkMoves(filteredMoves, gameState);

    const validDefensiveMoves: MoveInfo[] = [];

    // First pass: Find ALL defensive moves
    for (const darkMove of darkMoves) {
      // Test this dark move against each winning path
      for (const winningMove of winningMoves) {
        if (this.canBlockPath(gameState, darkMove, winningMove)) {
          validDefensiveMoves.push(darkMove);
          break; // Don't test this move against other winning paths once we know it works
        }
      }
    }

    if (validDefensiveMoves.length === 0) {
      return null;
    }

    // If only one defensive move, take it (even if it hangs)
    if (validDefensiveMoves.length === 1) {
      return validDefensiveMoves[0];
    }

    // Second pass: Prefer non-hanging defensive moves

    for (const defensiveMove of validDefensiveMoves) {
      const isHanging = this.isMovePieceHanging(defensiveMove, gameState);

      if (!isHanging) {
        return defensiveMove;
      }
    }

    // All defensive moves hang pieces - take the first one as a last resort
    return validDefensiveMoves[0];
  }

  /**
   * CRITICAL: Extract dark moves from the already-filtered context moves
   * This ensures we respect higher priority filtering (e.g., RaceBlockRemain)
   */
  private filterToDarkMoves(
    filteredMoves: MoveInfo[],
    gameState: GameState
  ): MoveInfo[] {
    // Filter the pre-filtered moves to only dark pieces
    return filteredMoves.filter((moveInfo) => {
      const piece = getPieceFromPosition(gameState, moveInfo.fromPosition);
      return piece && !piece.isLight;
    });
  }

  /**
   * @deprecated - DO NOT USE: This bypasses higher priority filtering
   * Use filterToDarkMoves(context.allPossibleMoves) instead
   */
  private getAllDarkMoves(gameState: GameState): MoveInfo[] {
    throw new Error(
      "ARCHITECTURE VIOLATION: getAllDarkMoves bypasses higher priority filters. Use filterToDarkMoves(context.allPossibleMoves) instead."
    );

    // Create temp state where it's dark's turn
    const tempState = cloneFullGameState(gameState);
    tempState.isLightTurn = false;

    const moveService = MCTSMoveGenerationService.getInstance();
    const allMoves = moveService.getAllMoves(tempState);

    // Filter to only dark moves
    return allMoves.filter((moveInfo) => {
      const piece = getPieceFromPosition(tempState, moveInfo.fromPosition);
      return piece && !piece.isLight;
    });
  }

  private canBlockPath(
    gameState: GameState,
    darkMove: MoveInfo,
    lightWinningMove: MoveInfo
  ): boolean {
    // Ply 1: Apply our dark move
    const stateAfterOurMove = cloneFullGameState(gameState);
    stateAfterOurMove.isLightTurn = false;

    const darkPiece = getPieceFromPosition(
      stateAfterOurMove,
      darkMove.fromPosition
    );
    if (!darkPiece) return false;

    applyMoveWithSnakeRespawn(
      stateAfterOurMove,
      darkMove.fromPosition,
      darkMove.move,
      darkPiece
    );
    validateMeadowEffects(stateAfterOurMove);
    stateAfterOurMove.isLightTurn = true; // Now light's turn

    // Ply 2: Simulate light king moving to threat square
    const stateAfterLightMove = cloneFullGameState(stateAfterOurMove);
    const lightPiece = getPieceFromPosition(
      stateAfterLightMove,
      lightWinningMove.fromPosition
    );
    if (!lightPiece) return false;

    applyMoveWithSnakeRespawn(
      stateAfterLightMove,
      lightWinningMove.fromPosition,
      lightWinningMove.move,
      lightPiece
    );
    validateMeadowEffects(stateAfterLightMove);
    stateAfterLightMove.isLightTurn = false; // Back to dark's turn

    const lightKingPosition = lightWinningMove.move.position;

    // Ply 3: Check if we can capture the light king
    const moveService = MCTSMoveGenerationService.getInstance();
    const ourResponses = moveService.getAllMoves(stateAfterLightMove);

    // Check if any of our responses can capture the light king
    for (const response of ourResponses) {
      if (
        response.move.position.row === lightKingPosition.row &&
        response.move.position.col === lightKingPosition.col
      ) {
        // We can capture the light king!
        return true;
      }
    }

    return false;
  }

  /**
   * Basic hanging detection for defensive move selection
   * Simplified version of the logic from HangingPiecesPrevention priority
   */
  private isMovePieceHanging(
    moveInfo: MoveInfo,
    gameState: GameState
  ): boolean {
    try {
      // Get the piece being moved
      const piece = getPieceFromPosition(gameState, moveInfo.fromPosition);
      if (!piece) {
        this.addLog(
          `🔍 TWO-TURN HANGING: No piece found at [${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}]`
        );
        return false;
      }

      // Skip king moves - race blocking is more important than king hanging
      if (piece.isKing) {
        this.addLog(
          `🔍 TWO-TURN HANGING: Skipping king hanging check (race blocking priority)`
        );
        return false;
      }

      this.addLog(
        `🔍 TWO-TURN HANGING: Testing if ${
          piece.isLight ? "Light" : "Dark"
        } piece move [${moveInfo.fromPosition.row},${
          moveInfo.fromPosition.col
        }] -> [${moveInfo.move.position.row},${
          moveInfo.move.position.col
        }] hangs piece`
      );

      // Simulate the move
      const testState = cloneFullGameState(gameState);
      const testPiece = getPieceFromPosition(testState, moveInfo.fromPosition);
      if (!testPiece) {
        this.addLog(`🔍 TWO-TURN HANGING: No test piece found`);
        return false;
      }

      this.addLog(
        `🔍 TWO-TURN HANGING: Before move - Turn state: isLightTurn = ${testState.isLightTurn}`
      );

      applyMoveWithSnakeRespawn(
        testState,
        moveInfo.fromPosition,
        moveInfo.move,
        testPiece
      );
      validateMeadowEffects(testState);

      // NOTE: applyMoveWithSnakeRespawn already handles turn switching
      // Do NOT manually switch turns again!

      this.addLog(
        `🔍 TWO-TURN HANGING: After move - Turn state: isLightTurn = ${testState.isLightTurn} (should be enemy turn)`
      );

      // Check if enemy can capture our moved piece
      const enemyMoves =
        MCTSMoveGenerationService.getInstance().getAllMoves(testState);
      const newPosition = moveInfo.move.position;

      this.addLog(
        `🔍 TWO-TURN HANGING: Found ${enemyMoves.length} enemy moves targeting our new position [${newPosition.row},${newPosition.col}]`
      );

      for (const enemyMove of enemyMoves) {
        if (
          enemyMove.move.position.row === newPosition.row &&
          enemyMove.move.position.col === newPosition.col
        ) {
          this.addLog(
            `🔍 TWO-TURN HANGING: Enemy can capture at [${newPosition.row},${newPosition.col}] - checking recapture...`
          );

          // Enemy can capture our piece - check if we can recapture
          const canRecapture = this.canRecaptureAfterCapture(
            testState,
            newPosition,
            enemyMove
          );

          if (!canRecapture) {
            this.addLog(
              `🔍 TWO-TURN HANGING: ❌ HANGING - Enemy can capture and we cannot recapture!`
            );
            return true; // Piece is hanging
          } else {
            this.addLog(
              `🔍 TWO-TURN HANGING: ✅ Trade - Enemy can capture but we can recapture`
            );
          }
        }
      }

      this.addLog(`🔍 TWO-TURN HANGING: ✅ SAFE - No enemy captures found`);
      return false; // Piece is not hanging
    } catch (error) {
      this.addLog(`⚠️ TWO-TURN HANGING: Hanging check error: ${error}`);
      return true; // Assume hanging on error for safety
    }
  }

  /**
   * Quick recapture check - can we recapture if enemy takes our piece?
   */
  private canRecaptureAfterCapture(
    testState: GameState,
    ourPiecePosition: Position,
    enemyCapturingMove: MoveInfo
  ): boolean {
    try {
      // Simulate enemy capturing our piece
      const stateAfterCapture = cloneFullGameState(testState);
      const enemyPiece = getPieceFromPosition(
        stateAfterCapture,
        enemyCapturingMove.fromPosition
      );
      if (!enemyPiece) return false;

      applyMoveWithSnakeRespawn(
        stateAfterCapture,
        enemyCapturingMove.fromPosition,
        enemyCapturingMove.move,
        enemyPiece
      );
      validateMeadowEffects(stateAfterCapture);
      stateAfterCapture.isLightTurn = !stateAfterCapture.isLightTurn; // Back to our turn

      // Check if we can capture the enemy piece that captured ours
      const ourResponseMoves =
        MCTSMoveGenerationService.getInstance().getAllMoves(stateAfterCapture);
      const enemyNewPosition = enemyCapturingMove.move.position;

      for (const ourMove of ourResponseMoves) {
        if (
          ourMove.move.position.row === enemyNewPosition.row &&
          ourMove.move.position.col === enemyNewPosition.col
        ) {
          return true; // We can recapture
        }
      }

      return false; // Cannot recapture
    } catch (error) {
      return false; // Assume no recapture on error
    }
  }
}

